/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent } from "react";

import { StrapiCareersPage } from "@/types/strapi";

import { Colors } from "@/tokens/color";
import { ButtonVariantKeys } from "@/tokens/configs/button_config";
import { AllIcons } from "@/tokens/configs/icon_config";
import { Spacing } from "@/tokens/spacing";

import { Button } from "@/ui/atoms/button";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Icon } from "@/ui/atoms/icon";
import { Media } from "@/ui/atoms/media";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";

import { buildStylesByBreakpoint } from "@/util/style_util";
import { convertToRem } from "@/util/ui_util";

interface CareersWorkProps extends StrapiCareersPage {
    className?: SerializedStyles;
}

export const CareersWork: FunctionComponent<CareersWorkProps> = (props) => {
    /**
     * Styles
     */
    const subHeadlineStyles = css(
        buildStylesByBreakpoint("marginRight", {
            extraSmall: Spacing["spacing-3"],
            large: Spacing["spacing-4"],
        }),
        {
            height: "fit-content",
            position: "relative",
            width: "1em",
        },
    );

    const iconStyles = css({
        alignContent: "center",
        display: "flex",
        left: "50%",
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
    });

    const CTAStyles = css(
        buildStylesByBreakpoint("display", {
            medium: "flex",
        }),
        buildStylesByBreakpoint("alignItems", {
            medium: "end",
        }),
        buildStylesByBreakpoint("justifyContent", {
            medium: "flex-end",
        }),
    );

    const valuesStyles = css(
        buildStylesByBreakpoint("columns", {
            extraSmall: 1,
            medium: 2,
        }),
        {
            columnGap: Spacing["spacing-8"],
            overflow: "hidden",
        },
    );

    const valueStyles = css(
        buildStylesByBreakpoint("padding", {
            extraSmall: `${Spacing["spacing-8"]} 0`,
            large: `${Spacing["spacing-12"]} 0`,
        }),
        {
            breakInside: "avoid-column",
            display: "flex",
        },
        {
            "& + li div::before": css(
                buildStylesByBreakpoint("top", {
                    extraSmall: `-${Spacing["spacing-8"]}`,
                    large: `-${Spacing["spacing-12"]}`,
                }),
                {
                    borderTop: `1px solid ${Colors["lighten-20"]}`,
                    content: "''",
                    position: "absolute",
                    width: "100%",
                },
            ),
        },
    );

    const imageBlockStyles = css(
        buildStylesByBreakpoint("columnGap", {
            extraSmall: Spacing["spacing-5"],
            medium: Spacing["spacing-6"],
            large: Spacing["spacing-8"],
        }),
        buildStylesByBreakpoint("display", {
            large: "flex",
        }),
        {
            columns: 2,
        },
    );

    const pictureWrapperStyles = (_index: number) =>
        css(
            buildStylesByBreakpoint("paddingTop", {
                extraSmall: _index === 0 ? "50%" : 0,
                medium: _index === 2 ? "18%" : 0,
                large: 0,
            }),
            buildStylesByBreakpoint("width", {
                extraSmall: "100%",
                large: [1, 2].includes(_index) ? "18.5vw" : "31.25vw",
            }),
            buildStylesByBreakpoint("marginTop", {
                extraSmall: _index % 2 ? Spacing["spacing-6"] : 0,
                large: (() => {
                    switch (_index) {
                        case 1:
                            return convertToRem(281);
                        case 2:
                            return convertToRem(81);
                        case 3:
                            return convertToRem(200);
                        default:
                            return 0;
                    }
                })(),
            }),
            {
                breakInside: "avoid-column",
                display: "flex",
                flexDirection: _index == 1 ? "row-reverse" : "row",
            },
        );

    const pictureStyles = (_index: number) =>
        css(
            buildStylesByBreakpoint("width", {
                extraSmall: "100%",
                medium: [1, 2].includes(_index)
                    ? /*
                       * The left hand side of the addition operation calculates the portion of width from gap
                       * and multiplies it by two since it spans two gaps. The right hand side of the addition
                       * operation calculates the width of a grid column. The full width of the container for
                       * the image spans five columns with four gaps. We take 100% and subtract four gaps worth,
                       * and then evenly divide the rest by five. The image spans three columns, therefore
                       * we multiply by three.
                       */
                      ` calc(2 * (${Spacing["spacing-6"]}) + 3 * (100% - 4 * ${Spacing["spacing-6"]}) / 5)`
                    : "100%",
                large: "100%",
            }),
            buildStylesByBreakpoint("height", {
                large: 0,
            }),
            buildStylesByBreakpoint("paddingBottom", {
                large: "100%",
            }),
        );

    /**
     * Rendering
     */
    const renderCTA = () => (
        <GridColumn
            className={CTAStyles}
            columnSpan={{ extraSmall: "full", medium: 6, large: 5 }}
            columnStart={{ extraSmall: 1, medium: 7 }}
            marginTop="CareersWorkCTA"
        >
            <Button
                className={css({
                    width: "fit-content",
                })}
                href={props.How_We_Work_CTA?.URL}
                variant={
                    props.How_We_Work_CTA?.Appearance.toLowerCase() as ButtonVariantKeys
                }
            >
                {props.How_We_Work_CTA?.Text}
            </Button>
        </GridColumn>
    );

    const renderValuesList = () => (
        <GridColumn
            className={valuesStyles}
            columnSpan={{ extraSmall: "full", large: 10 }}
            columnStart={{ extraSmall: 1, large: 2 }}
            marginTop="CareersWorkValuesItemsContainer"
            tag="div"
        >
            <ul
                css={css({
                    transform: `translateY(-${convertToRem(32)})`,
                })}
            >
                {props.How_We_Work_Values.map((_value) => (
                    <li
                        css={valueStyles}
                        key={`careers-how-we-work::value::${_value.id}`}
                    >
                        <Text
                            className={subHeadlineStyles}
                            fontSize="CareerSubheadline"
                            tag="span"
                        >
                            &nbsp;
                            <Icon
                                className={iconStyles}
                                color="green-20"
                                slug={_value.Icon as AllIcons}
                            />
                        </Text>

                        <div
                            css={css({
                                position: "relative",
                                width: "100%",
                            })}
                        >
                            <Text fontSize="CareerSubheadline" tag="h3">
                                {_value.Headline}
                            </Text>

                            <Text marginTop="CareersWorkValuesDescription">
                                {_value.Description}
                            </Text>
                        </div>
                    </li>
                ))}
            </ul>
        </GridColumn>
    );

    const renderImageBlock = () => (
        <GridColumn
            className={imageBlockStyles}
            columnSpan={{
                extraSmall: "full",
                medium: 10,
                large: 12,
            }}
            columnStart={{
                extraSmall: 1,
                medium: 2,
                large: 1,
            }}
            marginBottom="CareersContainerMargin"
            marginTop="CareersContainerMargin"
        >
            {props.Team_Pictures.map((_picture, _index) => (
                <div
                    css={pictureWrapperStyles(_index)}
                    key={`careers-work::team-pictures::${_picture.id}`}
                >
                    <Media className={pictureStyles(_index)} {..._picture} />
                </div>
            ))}
        </GridColumn>
    );

    return (
        <GridContainer>
            <GridColumn
                columnSpan={{ extraSmall: "full", medium: 6, large: 5 }}
                columnStart={{ extraSmall: 1, large: 2 }}
            >
                <Text fontSize="CareerHeadline" tag="h2">
                    {props.How_We_Work_Headline}
                </Text>

                <RichText
                    className={css({ marginTop: Spacing["spacing-4"] })}
                    config="Longform"
                >
                    {props.How_We_Work_Subheadline}
                </RichText>
            </GridColumn>

            {props.How_We_Work_CTA && renderCTA()}

            {renderValuesList()}

            {renderImageBlock()}
        </GridContainer>
    );
};
